/*
 * Candy Club drop clock.
 *
 * Works out, on its own, when the next drop opens: the configured day of the
 * month at the configured time, in an explicit UTC offset, with a fixed window
 * after it. Once a window closes it rolls straight to the following month, so
 * the page never needs a date edited into it again.
 *
 * It publishes the result as data-drop-state on <html>, which the stylesheet
 * uses to show either the teaser or the live product. Liquid sets the same
 * attribute from the server clock first, so there is no flash of the wrong
 * state on load.
 */
if (!customElements.get('candy-drop')) {
  customElements.define(
    'candy-drop',
    class CandyDrop extends HTMLElement {
      connectedCallback() {
        this.day = parseInt(this.dataset.day, 10) || 1;
        this.time = this.dataset.time || '09:00';
        this.offset = this.dataset.offset || '+10:00';
        this.windowMs = (parseFloat(this.dataset.windowHours) || 48) * 3600000;
        this.force = this.dataset.force || 'auto';

        this.units = {
          days: this.querySelector('[data-unit="days"]'),
          hours: this.querySelector('[data-unit="hours"]'),
          minutes: this.querySelector('[data-unit="minutes"]'),
          seconds: this.querySelector('[data-unit="seconds"]')
        };
        this.label = this.querySelector('[data-drop-label]');

        this.tick();
        this.timer = setInterval(() => this.tick(), 1000);
      }

      disconnectedCallback() {
        clearInterval(this.timer);
      }

      /* Start of the drop for a given year and month, as an epoch. */
      startFor(year, month) {
        const mm = String(month + 1).padStart(2, '0');
        const dd = String(this.day).padStart(2, '0');
        return Date.parse(`${year}-${mm}-${dd}T${this.time}:00${this.offset}`);
      }

      /* The window we are currently in or heading towards. */
      currentWindow(now) {
        const d = new Date(now);
        let year = d.getUTCFullYear();
        let month = d.getUTCMonth();

        let start = this.startFor(year, month);

        // Already past this month's window, so the next one is what matters.
        if (now >= start + this.windowMs) {
          month += 1;
          if (month > 11) {
            month = 0;
            year += 1;
          }
          start = this.startFor(year, month);
        } else if (now < start) {
          // Before this month's drop. Check the previous month's window has not
          // just rolled over midnight into today.
          let prevYear = year;
          let prevMonth = month - 1;
          if (prevMonth < 0) {
            prevMonth = 11;
            prevYear -= 1;
          }
          const prevStart = this.startFor(prevYear, prevMonth);
          if (now < prevStart + this.windowMs) start = prevStart;
        }

        return { start: start, end: start + this.windowMs };
      }

      tick() {
        const now = Date.now();
        const win = this.currentWindow(now);

        if (Number.isNaN(win.start)) {
          clearInterval(this.timer);
          return;
        }

        let state = now >= win.start && now < win.end ? 'open' : 'teaser';
        if (this.force === 'teaser' || this.force === 'open') state = this.force;

        document.documentElement.setAttribute('data-drop-state', state);

        if (this.label) {
          this.label.textContent =
            state === 'open' ? this.dataset.labelOpen || 'Closes in' : this.dataset.labelTeaser || 'Drops in';
        }

        // When forced, there is no meaningful deadline to count towards.
        const target = state === 'open' ? win.end : win.start;
        const remaining = Math.max(0, target - now);
        const seconds = Math.floor(remaining / 1000);

        const values = {
          days: Math.floor(seconds / 86400),
          hours: Math.floor((seconds % 86400) / 3600),
          minutes: Math.floor((seconds % 3600) / 60),
          seconds: seconds % 60
        };

        Object.entries(values).forEach(([unit, value]) => {
          const node = this.units[unit];
          if (!node) return;
          const text = unit === 'days' ? String(value) : String(value).padStart(2, '0');
          if (node.textContent !== text) node.textContent = text;
        });
      }
    }
  );
}
